import { DEFAULT_PAGE_SIZE } from "@tfg/ui"
import { useCallback, useState } from "react"

export type CursorBasedPagination = {
  skip: number
  take: number
}

export type PageBasedPagination = {
  pageIndex: number
  pageSize: number
}

type PaginationProps = {
  pagination?: CursorBasedPagination
}

export const usePagination = (props: PaginationProps = {}) => {
  const [pagination, setPagination] = useState<CursorBasedPagination>({
    skip: props.pagination?.skip ?? 0,
    take: props.pagination?.take ?? DEFAULT_PAGE_SIZE,
  })

  const calculateTotalPages = useCallback((take: number, count: number) => {
    return Math.ceil(count / take)
  }, [])

  const setPageBasedPagination = useCallback(
    (
      newPagination:
        | PageBasedPagination
        | ((oldPagination: PageBasedPagination) => PageBasedPagination)
    ): void => {
      if (typeof newPagination === "function") {
        setPagination((oldCursorBasedPagination) => {
          const newPageBasedPagination = newPagination({
            pageIndex:
              oldCursorBasedPagination.skip / oldCursorBasedPagination.take,
            pageSize: oldCursorBasedPagination.take,
          })

          return {
            skip:
              newPageBasedPagination.pageIndex *
              newPageBasedPagination.pageSize,
            take: newPageBasedPagination.pageSize,
          }
        })
      } else {
        setPagination({
          skip: newPagination.pageIndex * newPagination.pageSize,
          take: newPagination.pageSize,
        })
      }
    },
    []
  )

  const resetPagination = useCallback(() => {
    setPagination({
      skip: 0,
      take: DEFAULT_PAGE_SIZE,
    })
  }, [])

  const goToFirstPage = useCallback(() => {
    setPagination((oldPagination) => ({
      ...oldPagination,
      skip: 0,
    }))
  }, [])

  return {
    pageBasedPagination: {
      pageIndex: pagination.skip / pagination.take,
      pageSize: pagination.take,
    },
    cursorBasedPagination: {
      skip: pagination.skip,
      take: pagination.take,
    },
    pagination,
    calculateTotalPages,
    setPageBasedPagination,
    setPagination,
    resetPagination,
    goToFirstPage,
  }
}
